import { useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Trophy, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Share2, Twitter, Facebook, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Read-only Power Rankings widget for the public site.
 *
 * Props:
 *   compact (bool) — if true, default-collapsed to top 10. Click to expand.
 *   showFooter (bool) — show updated-at footer text.
 */
export default function PowerRankings({ compact = true, showFooter = true }) {
  const [rows, setRows] = useState([]);
  const [titansAbbr, setTitansAbbr] = useState("TEN");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/power-rankings");
        setRows(data.rows || []);
        setTitansAbbr(data.titans_abbr || "TEN");
        setUpdatedAt(data.updated_at);
      } catch (e) {
        console.error("PowerRankings load failed:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const titansRow = useMemo(
    () => rows.find((r) => (r.abbr || "").toUpperCase() === titansAbbr),
    [rows, titansAbbr],
  );

  const visible = useMemo(() => (expanded ? rows : rows.slice(0, 10)), [rows, expanded]);

  const shareUrl = `${BACKEND}/api/share/power-rankings`;
  const ogImage = `${BACKEND}/api/og/power-rankings.png`;
  const shareText = "NFL Power Rankings · The Titan Standard";

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      console.error("PowerRankings copy failed:", e);
      toast.error("Copy failed");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareText, url: shareUrl }); } catch { /* user cancelled */ }
    } else { copyLink(); }
  };

  if (loading) return <div className="text-titans-silver">Loading rankings…</div>;
  if (!rows.length) return null;

  return (
    <div className="titans-card overflow-hidden" data-testid="power-rankings">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[rgba(138,141,143,0.15)] flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Trophy size={18} className="text-titans-red" />
          <div>
            <div className="font-cond uppercase tracking-[0.3em] text-[10px] text-titans-blue">Editor&apos;s Take</div>
            <div className="font-display text-xl md:text-2xl uppercase tracking-wide leading-none mt-1">
              NFL Power Rankings
            </div>
          </div>
        </div>
        {titansRow && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShareOpen((v) => !v)}
              data-testid="power-share-btn"
              className="inline-flex items-center gap-1.5 border border-titans-blue/40 text-titans-blue hover:text-white hover:bg-titans-blue/20 px-3 py-1.5 font-cond uppercase tracking-wider text-[10px]"
              aria-label="Share rankings"
            >
              <Share2 size={12} /> Share
            </button>
            <div
              data-testid="titans-rank-badge"
              className="inline-flex items-center gap-2 border border-titans-red bg-titans-red/10 px-3 py-1.5"
            >
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Titans</div>
              <div className="font-display text-2xl text-white leading-none">#{titansRow.rank}</div>
              <MovementIcon row={titansRow} />
            </div>
          </div>
        )}
      </div>

      {/* Share panel */}
      {shareOpen && (
        <div className="px-5 py-4 border-b border-[rgba(138,141,143,0.15)] bg-titans-void/40" data-testid="power-share-panel">
          <div className="grid md:grid-cols-[300px_1fr] gap-4 items-start">
            <img
              src={`${ogImage}?ts=${updatedAt || ""}`}
              alt="Share card preview"
              loading="lazy"
              data-testid="power-share-preview"
              className="w-full h-auto block border border-titans-elev"
            />
            <div>
              <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue mb-2">Drop this card anywhere</div>
              <p className="text-titans-silver text-xs font-body mb-3">
                The card auto-refreshes whenever you save new rankings. Posts a Titans-highlighted
                preview on X, Facebook, iMessage and Discord.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="power-share-twitter"
                  className="bg-titans-navy hover:bg-titans-elev border border-titans-elev text-white font-cond uppercase tracking-wider text-[10px] px-2 py-2.5 inline-flex items-center justify-center gap-1.5"
                >
                  <Twitter size={12} /> X
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="power-share-facebook"
                  className="bg-titans-navy hover:bg-titans-elev border border-titans-elev text-white font-cond uppercase tracking-wider text-[10px] px-2 py-2.5 inline-flex items-center justify-center gap-1.5"
                >
                  <Facebook size={12} /> Facebook
                </a>
                <button
                  onClick={copyLink}
                  data-testid="power-share-copy"
                  className={`font-cond uppercase tracking-wider text-[10px] px-2 py-2.5 inline-flex items-center justify-center gap-1.5 ${
                    copied ? "bg-titans-blue text-white" : "bg-titans-red hover:bg-titans-red-hover text-white"
                  }`}
                >
                  {copied ? <><Check size={12} /> Copied</> : <><LinkIcon size={12} /> Copy</>}
                </button>
                <button
                  onClick={nativeShare}
                  data-testid="power-share-native"
                  className="border border-titans-silver/40 text-titans-silver hover:text-white hover:border-white font-cond uppercase tracking-wider text-[10px] px-2 py-2.5 inline-flex items-center justify-center gap-1.5"
                >
                  <Share2 size={12} /> Quick Share
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rows */}
      <div data-testid="power-rankings-list">
        {visible.map((r) => {
          const isTen = (r.abbr || "").toUpperCase() === titansAbbr;
          return (
            <div
              key={r.abbr}
              data-testid={`power-row-${r.abbr}`}
              className={`flex items-center gap-3 px-5 py-3 border-b border-[rgba(138,141,143,0.1)] last:border-0 ${
                isTen ? "bg-titans-red/15 border-l-4 border-l-titans-red" : ""
              }`}
            >
              <div className={`font-display text-2xl w-9 ${isTen ? "text-titans-red" : "text-titans-silver"}`}>
                {r.rank}
              </div>
              <MovementIcon row={r} />
              {r.logo && (
                <img src={r.logo} alt={r.abbr} className="h-8 w-8 object-contain flex-shrink-0" loading="lazy" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`font-display text-sm md:text-base uppercase tracking-wide leading-tight ${isTen ? "text-white" : "text-titans-silver"}`}>
                  {r.short_name || r.name}
                </div>
                {r.blurb && (
                  <div className="text-titans-silver/80 text-xs md:text-sm font-body mt-0.5 leading-snug truncate">
                    {r.blurb}
                  </div>
                )}
              </div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-muted flex-shrink-0 hidden sm:block">
                {r.abbr}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand toggle */}
      {compact && rows.length > 10 && (
        <button
          onClick={() => setExpanded((e) => !e)}
          data-testid="power-rankings-toggle"
          className="w-full px-5 py-3 border-t border-[rgba(138,141,143,0.15)] font-cond uppercase tracking-wider text-xs text-titans-blue hover:bg-titans-elev/50 inline-flex items-center justify-center gap-2"
        >
          {expanded ? <><ChevronUp size={14} /> Collapse</> : <><ChevronDown size={14} /> See All 32</>}
        </button>
      )}

      {/* Footer */}
      {showFooter && updatedAt && (
        <div className="px-5 py-2 bg-titans-void/40 border-t border-[rgba(138,141,143,0.1)] text-titans-muted text-[10px] font-cond uppercase tracking-wider">
          Updated {formatDate(updatedAt, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          {" · "}Editorial ranking by The Titan Standard
        </div>
      )}
    </div>
  );
}

function MovementIcon({ row }) {
  const delta = (row.prev_rank || row.rank) - row.rank;
  if (delta > 0) {
    return (
      <div className="inline-flex items-center gap-0.5 text-green-400 flex-shrink-0">
        <TrendingUp size={12} />
        <span className="font-cond text-[10px]">{delta}</span>
      </div>
    );
  }
  if (delta < 0) {
    return (
      <div className="inline-flex items-center gap-0.5 text-titans-red flex-shrink-0">
        <TrendingDown size={12} />
        <span className="font-cond text-[10px]">{Math.abs(delta)}</span>
      </div>
    );
  }
  return <Minus size={12} className="text-titans-muted flex-shrink-0" />;
}
